import UsedBy from '../UsedBy';
import Loading from '../Loading';
import GS from '../../grafsync/grafsync';
import { makeCancelable } from '../utils/utils';
import { CardHeader } from '@material-ui/core';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import DeleteIcon from '@material-ui/icons/Delete';
import PropTypes from 'prop-types';
import React from 'react';
import { NavLink } from 'react-router-dom';
import AreYouSure from '../../core/AreYouSure';
import { globalStyles, gstheme } from '../../core/style/gstheme';
import { checkResourceEntitlements } from '../../core/utils/utils';
import withGS from '../../core/withGShoc';

const styles = theme => {
  return Object.assign({
    projectCard: {
      maxWidth: '282px',
      margin: 'auto',
      boxShadow: '0px 1px 3px #00000033',
      '&:hover': {
        boxShadow: '0px 12px 17px #00000024'
      }
    },
    projectCardHeadingContent: {
      maxHeight: '90px',
      overflow: 'hidden'
    },
    projectCardHeading: {
      color: 'white',
      fontFamily: 'rajdhani',
      fontSize: '24px',
      fontWeight: 500,
      textAlign: 'center',
      backgroundColor: theme.palette.primary.main,
      height: '98px'
    },
    media: {
      height: 150,
    },
    projectCardContent: {
      height: '60px',
      overflow: 'auto'
    },
    noContent: {
      height: '5em',
      overflow: 'auto',
      paddingRight: 5,
      fontStyle: 'italic'
    },
    actions: {
      display: 'flex',
      justifyContent: 'space-between'
    },
    readOnlyActions: {
      display: 'flex',
      justifyContent: 'flex-end'
    },
  }, globalStyles);
};

class ProjectCard extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      areYouSure: false,
      isUsed: false,
      checking: false
    };
    this.cancellable = null;
  }

  doSearch() {
    this.cancellable = makeCancelable(
      GS.search({
        search: [{
          predicate: "https://grafsync.graphifi.com/schema/vm/associatedProject",
          value: this.props.id
        }],
        noCount: true,
        page: 1,
        pageSize: 20
      })
    );
    return this.cancellable;
  }

  checkIfSure = () => {
    this.setState({ checking: true }, async () => {
      try {
        const isUsed = await this.doCheck({
          value: this.props.id,
          predicate: "https://grafsync.graphifi.com/schema/vm/associatedProject"
        }).promise;
        if (this.cancellable && this.cancellable.isCancelled()) return;
        if (isUsed) {
          const data = await this.doSearch().promise;
          this.setState({ usedResources: data.results, isUsed: true });
        }
        else this.setState({ areYouSure: true });
        this.setState({ checking: false });
      }
      catch (e) {
        if (this.cancellable && this.cancellable.isCancelled()) return;
        this.setState({ checking: false });
      }
    });
  };

  onSure = () => {
    this.setState({ areYouSure: false });
    this.props.onDeleteProject(this.props.data.id);
  };

  onNotSure = () => this.setState({ areYouSure: false });

  doCheck(payload) {
    this.cancellable = makeCancelable(GS.checkForInbound(payload));
    return this.cancellable;
  }

  onExitWarning = () => this.setState({ isUsed: false });

  render() {

    const { classes, data, lang, applicationURL, entitlement, locked } = this.props;
    const { areYouSure, checking, isUsed, usedResources } = this.state;

    const isReadOnly = checkResourceEntitlements(data, entitlement);
    const title = data.title && data.title[lang] ? data.title[lang] : "No project title in chosen language";
    const desc = data.description && data.description[lang] ? data.description[lang] : '';
    const colour = data.projectColour ? '#' + data.projectColour : gstheme.palette.primary.main;

    if (checking) return <Loading />;

    const cardHeader = <CardHeader
      data-test="project-title"
      disableTypography
      classes={{
        content: classes.projectCardHeadingContent
      }}
      className={classes.projectCardHeading}
      style={{ backgroundColor: colour }}
      title={title} />;

    const created = data.created ? (new Date(data.created)).toLocaleDateString() + " " + (new Date(data.created)).toLocaleTimeString()
      :
      "- Recently";

    // This should be language based on user preference not project
    return (
      <Card data-test={title} className={classes.projectCard} name="projectCard">
        {locked ?
          cardHeader
          :
          <NavLink to={applicationURL + "/project/detail#project=" + data.id} className={classes.cardLink}>{cardHeader}</NavLink>
        }
        <CardContent className={classes.projectCardContent}>
          <div style={{ marginTop: -12, marginBottom: 6 }}>
            <Typography data-test="created-date" variant="caption" align="right">Created {created}</Typography>
          </div>
          <Typography data-test="project-description" variant="body2">{desc}</Typography>
        </CardContent>
        <CardActions className={!isReadOnly ? classes.actions : classes.readOnlyActions}>
          {locked && <Typography data-test="project-locked" variant="body2">This project is currently locked.</Typography>}
          {!isReadOnly && !locked &&
            <Tooltip placement="right" title='Delete this project'>
              <IconButton data-test="delete-project" name="deleteButton" onClick={this.checkIfSure} className={classes.deleteButton}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          }
          <IconButton disabled={locked} data-test="go-to-project" size="small" style={{ float: 'right', width: 40, height: 40, verticalAlign: 'middle', paddingTop: 8 }}>
            <NavLink to={applicationURL + "/project/detail#project=" + data.id} className={classes.cardLink}>
              <ArrowForwardIcon className={classes.actionButton} />
            </NavLink>
          </IconButton>
        </CardActions>
        {isUsed && <UsedBy
          title="We Need to Sort Something First..."
          usedResources={usedResources}
          graph={data.inNamedGraph}
          defaultLang={data.defaultLang}
          message={[
            "This project is used by at least one other project.",
            "Before you can delete this project you must remove any associations. If any projects have recently been deleted that were associated to this project there may be a delay before you can delete this project. Please try again later if that is the case."]}
          onExit={this.onExitWarning} />}
        {areYouSure && <AreYouSure onSure={this.onSure} onNotSure={this.onNotSure} message="Do you really want to delete this project?" />}
      </Card>
    );
  }
}

ProjectCard.defaultProps = {
  locked: false
};

ProjectCard.propTypes = {
  id: PropTypes.string.isRequired,
  classes: PropTypes.object,
  onDeleteProject: PropTypes.func.isRequired,
  locked: PropTypes.bool,
  applicationURL: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  entitlement: PropTypes.string.isRequired,
  lang: PropTypes.string.isRequired
};

export default withStyles(styles)(withGS(ProjectCard));